import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Award, Eye, Ruler, AlertTriangle, MessageCircle, Mail, Phone } from 'lucide-react';

const GradesPage = () => {
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const grades = [
        {
            grade: "A",
            color: "emerald",
            title: "Excellent état",
            distance: "20 cm",
            description: "L'appareil ne présente aucun défaut visible à environ 20 cm de distance. Des micro-griffures peuvent être présentes mais restent imperceptibles à l'œil nu dans des conditions normales d'utilisation.",
            details: [
                "Aucun défaut visible à 20 cm",
                "Micro-griffures possibles",
                "Aspect quasi neuf"
            ]
        },
        {
            grade: "B",
            color: "blue",
            title: "Bon état",
            distance: "50 cm (à bout de bras)",
            description: "Les appareils sont considérés en bon état correct. Des griffures, chocs ou traces d'usure peuvent être présents mais restent invisibles sur l'appareil à environ 50 cm (à bout de bras).",
            details: [
                "Bon état général",
                "Griffures/chocs invisibles à 50 cm",
                "Usure légère possible"
            ]
        },
        {
            grade: "C",
            color: "amber",
            title: "État correct",
            distance: "Visible à bout de bras",
            description: "Des griffures, chocs et traces d'usure sont présents et visibles même à bout de bras, mais de manière modérée. L'appareil reste fonctionnel et présentable.",
            details: [
                "Griffures/chocs visibles",
                "Usure modérée",
                "Appareil fonctionnel"
            ]
        },
        {
            grade: "D",
            color: "red",
            title: "État d'usage",
            distance: "Nettement visible",
            description: "Les griffures, chocs et traces d'usure sont nettement prononcés et bien visibles. L'appareil présente des signes d'utilisation intensive.",
            details: [
                "Défauts bien visibles",
                "Usure prononcée",
                "Signes d'utilisation intensive"
            ]
        }
    ];

    const getGradeColors = (color) => {
        const colors = {
            emerald: {
                bg: "bg-emerald-100",
                border: "border-emerald-300",
                text: "text-emerald-700",
                badge: "bg-emerald-500",
                icon: "text-emerald-600"
            },
            blue: {
                bg: "bg-blue-100",
                border: "border-blue-300",
                text: "text-blue-700",
                badge: "bg-blue-500",
                icon: "text-blue-600"
            },
            amber: {
                bg: "bg-amber-100",
                border: "border-amber-300",
                text: "text-amber-700",
                badge: "bg-amber-500",
                icon: "text-amber-600"
            },
            red: {
                bg: "bg-red-100",
                border: "border-red-300",
                text: "text-red-700",
                badge: "bg-red-500",
                icon: "text-red-600"
            }
        };
        return colors[color];
    };

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <header className="bg-white border-b border-slate-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <Link to="/" className="inline-flex items-center text-slate-600 hover:text-slate-900">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Retour à l'accueil
                    </Link>
                </div>
            </header>

            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8">
                    {/* Title */}
                    <div className="flex items-center gap-4 mb-8">
                        <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center">
                            <Award className="w-7 h-7 text-blue-600" />
                        </div>
                        <div>
                            <h1 className="text-2xl font-bold text-slate-900">Guide des Grades Cosmétiques</h1>
                            <p className="text-slate-500">Comprendre notre système de classification</p>
                        </div>
                    </div>

                    {/* Introduction */}
                    <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 mb-8">
                        <div className="flex items-start gap-3">
                            <Eye className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <p className="font-semibold text-blue-900 mb-1">Évaluation visuelle standardisée</p>
                                <p className="text-sm text-blue-700">
                                    Nos grades cosmétiques sont basés sur l'évaluation visuelle de l'état esthétique de chaque appareil. 
                                    La distance d'observation est le critère principal pour déterminer le grade.
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Grades Grid */}
                    <div className="space-y-6 mb-10">
                        {grades.map((item) => {
                            const colors = getGradeColors(item.color);
                            return (
                                <div 
                                    key={item.grade}
                                    className={`${colors.bg} ${colors.border} border-2 rounded-xl p-6 transition-all hover:shadow-md`}
                                >
                                    <div className="flex items-start gap-4">
                                        <div className={`${colors.badge} text-white w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0`}>
                                            <span className="text-2xl font-bold">{item.grade}</span>
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className={`text-xl font-bold ${colors.text}`}>{item.title}</h3>
                                                <span className={`${colors.bg} ${colors.text} text-xs font-medium px-2 py-1 rounded-full border ${colors.border}`}>
                                                    <Ruler className="w-3 h-3 inline mr-1" />
                                                    {item.distance}
                                                </span>
                                            </div>
                                            <p className="text-slate-700 mb-3">{item.description}</p>
                                            <ul className="flex flex-wrap gap-2">
                                                {item.details.map((detail, idx) => (
                                                    <li 
                                                        key={idx}
                                                        className="bg-white/70 text-slate-600 text-sm px-3 py-1 rounded-full"
                                                    >
                                                        {detail}
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Important Notice */}
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
                        <div className="flex items-start gap-3">
                            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                                <h3 className="font-bold text-amber-900 mb-2">Informations importantes</h3>
                                <ul className="space-y-3 text-sm text-amber-800">
                                    <li>
                                        <strong>Indication à titre informatif :</strong> Les grades sont indiqués à titre indicatif sur les appareils fonctionnels ou à réparer pour vous permettre de sélectionner les appareils en fonction de vos besoins et d'évaluer le rapport qualité-prix de nos offres.
                                    </li>
                                    <li>
                                        <strong>Appareils cassés ou en panne :</strong> Le grade est également indiqué sur les appareils cassés ou en panne. Si un écran ou une face arrière est déclaré(e) cassé(e), alors le grade exclut cette partie de l'évaluation annoncée.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Contact Section */}
                    <div className="bg-slate-50 rounded-xl p-6">
                        <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2">
                            <MessageCircle className="w-5 h-5 text-blue-600" />
                            Besoin de précisions ?
                        </h3>
                        <p className="text-slate-600 mb-4">
                            N'hésitez pas à demander des précisions à votre chargé d'affaire ou à nous contacter directement :
                        </p>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <a 
                                href="mailto:welcome@phoneb2b.com"
                                className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-4 hover:border-blue-300 hover:shadow-sm transition-all"
                            >
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Mail className="w-5 h-5 text-blue-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Email</p>
                                    <p className="font-medium text-slate-900">welcome@phoneb2b.com</p>
                                </div>
                            </a>
                            <a 
                                href="https://wa.me/33648500231"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-4 hover:border-emerald-300 hover:shadow-sm transition-all"
                            >
                                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center">
                                    <Phone className="w-5 h-5 text-emerald-600" />
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">WhatsApp</p>
                                    <p className="font-medium text-slate-900">+33 6 48 50 02 31</p>
                                </div>
                            </a>
                        </div>
                    </div>

                    {/* Last update */}
                    <p className="text-center text-sm text-slate-400 mt-8 pt-4 border-t">
                        Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    </p>

                    {/* Back to home */}
                    <div className="mt-8 text-center">
                        <Link 
                            to="/" 
                            onClick={() => window.scrollTo(0, 0)}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-medium"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Retour à l'accueil
                        </Link>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default GradesPage;
